import { Modal } from "antd";
import cuid from "cuid";
import { defaults, get, keyBy, map } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_QUALIFICATION } from "utils/api/graphql/mutations/qualifications";
import { QUALIFIERS } from "utils/api/graphql/queries/qualifiers";
import { CONTACT_POINT_SYSTEM, QUALIFIER_TAGS } from "utils/constants";
import View from "./View";

const HelpModal = ({
  open,
  setOpen,
  project,
  onSubmitModal,
  context,
  trigger,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.HELP_MODAL", { returnObjects: true });

  const telecoms = defaults(keyBy(get(project, "telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
    [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
  });

  const { data, loading, error } = useQuery(QUALIFIERS, {
    variables: {
      where: {
        OR: [
          { tag: QUALIFIER_TAGS.NO_TERMINATION },
          { tag: QUALIFIER_TAGS.OLD_CONTRACT },
        ],
      },
    },
  });

  const qualifierSelected = useMemo(() => {
    if (!data?.qualifiers?.data) return null;
    const qualifiers = data.qualifiers.data;

    if (trigger === "replaceContract") {
      return qualifiers.find((q) => q.tag === QUALIFIER_TAGS.OLD_CONTRACT);
    }

    if (trigger === "hasOldContractMoreThanYear") {
      return qualifiers.find((q) => q.tag === QUALIFIER_TAGS.NO_TERMINATION);
    }

    return null;
  }, [data, trigger]);

  const [addQualification] = useMutation(CREATE_QUALIFICATION, {
    refetchQueries: [
      {
        query: QUALIFIERS,
        awaitRefetchQueries: true,
        variables: {
          withDeleted: true,
          where: {
            qualifications: {
              contact: { id: get(project, "contact.id") },
            },
          },
        },
      },
    ],
  });

  const onSubmitFrom = ({
    showTelecoms,
    showAccept,
    contact: { telecoms, ...contact },
  }) => {
    const updatedTelecoms = map(telecoms, (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );
    const user = get(project, "user");
    const currentContact = {
      ...contact,
      id: get(project, "id") || cuid(),
      telecoms: updatedTelecoms,
      ...(user ? { user } : {}),
    };

    if (qualifierSelected?.id) {
      addQualification({
        variables: {
          data: {
            id: cuid(),
            qualifier: { id: qualifierSelected.id },
            contact: { id: currentContact.id },
          },
        },
      });
    }

    onSubmitModal({
      payload: {
        contact: currentContact,
        visible: true,
        fields: {
          ...project.fields,
          contactAgent: true,
        },
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      width={720}
      footer={null}
      className="lmfhelp__modal"
    >
      <View
        contact={{
          ...project,
          telecoms,
          user: get(project, "user"),
        }}
        setOpen={setOpen}
        onSubmit={onSubmitFrom}
        TRANSLATION={TRANSLATION}
        errorMessages={t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        })}
        trigger={trigger}
        context={context}
      />
    </Modal>
  );
};

export default HelpModal;
